// eslint-disable-next-line import/no-restricted-paths
import { color } from '@/components/lib/colors/color.semantic';

// Recursively flattens the nested color tree.
// { background: { neutral: { base: '#0E0F11' } } } → { 'background.neutral.base': '#0E0F11' }
type FlattenLeaves<T, Prefix extends string = ''> = T extends string
    ? Record<Prefix, string>
    : {
          [K in keyof T & string]: FlattenLeaves<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>;
      }[keyof T & string];

type UnionToIntersection<T> = (T extends any ? (arg: T) => void : never) extends (arg: infer R) => void ? R : never;

type RestyleColors = UnionToIntersection<FlattenLeaves<typeof color>>;

type PathMap<T, P extends string = ''> = {
    [K in keyof T & string]: T[K] extends object
        ? PathMap<T[K], P extends '' ? K : `${P}.${K}`>
        : P extends ''
        ? K
        : `${P}.${K}`;
};

function toPathMap<T extends object>(obj: T, prefix = ''): PathMap<T> {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const val = (obj as Record<string, unknown>)[key];
        out[key] = val && typeof val === 'object' ? toPathMap(val as object, path) : path;
    }
    return out as PathMap<T>;
}

const flattenObject = (obj: Record<string, any>, prefix = ''): Record<string, string> => {
    const result: Record<string, string> = {};

    for (const key in obj) {
        const value = obj[key];
        const nextKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            result[nextKey] = value;
        } else {
            Object.assign(result, flattenObject(value, nextKey));
        }
    }

    return result;
};

// for restyle usage
export const restyleColors = flattenObject(color) as RestyleColors;

// Theme token paths — use on restyle props (<Box backgroundColor=...>,
// <Text color=...>). Leaves resolve to the dotted key (e.g.
// 'background.neutral.base'), which restyle looks up in appTheme.colors.
export const colorToken = toPathMap(color);

export const colorValue = color;
